/**
 * Local audio analysis — mirrors iOS VoiceAnalysisService
 * Analyzes an audio blob client-side: FFT frequency detection, RMS volume, resonance ratio.
 * Returns 42-segment metrics for torus visualization.
 */

const NOISE_VOLUME_THRESHOLD = 0.02;
const MIN_FREQ = 60;
const MAX_FREQ = 1000;
const FFT_SIZE = 4096;
const NUM_SEGMENTS = 42;

/**
 * Convert frequency to musical note name
 */
function frequencyToNote(freq) {
  if (freq <= 0) return '-';
  const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const semitone = 12 * Math.log2(freq / 440) + 69;
  const noteIndex = Math.round(semitone) % 12;
  const octave = Math.floor(Math.round(semitone) / 12) - 1;
  return `${noteNames[(noteIndex + 12) % 12]}${octave}`;
}

/**
 * Compute RMS volume from PCM samples, normalized 0-1
 */
function computeRMS(samples) {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) {
    sum += samples[i] * samples[i];
  }
  return Math.sqrt(sum / samples.length);
}

/**
 * Cooley-Tukey radix-2 FFT (in-place)
 * Input: real/imag arrays of length N (must be power of 2)
 */
function fft(real, imag) {
  const n = real.length;
  if (n <= 1) return;

  // Bit-reversal permutation
  let j = 0;
  for (let i = 0; i < n; i++) {
    if (i < j) {
      [real[i], real[j]] = [real[j], real[i]];
      [imag[i], imag[j]] = [imag[j], imag[i]];
    }
    let m = n >> 1;
    while (m >= 1 && j >= m) {
      j -= m;
      m >>= 1;
    }
    j += m;
  }

  // Butterfly stages
  for (let size = 2; size <= n; size *= 2) {
    const half = size / 2;
    const step = -2 * Math.PI / size;
    for (let i = 0; i < n; i += size) {
      for (let k = 0; k < half; k++) {
        const angle = step * k;
        const wr = Math.cos(angle);
        const wi = Math.sin(angle);
        const idx1 = i + k;
        const idx2 = i + k + half;
        const tr = wr * real[idx2] - wi * imag[idx2];
        const ti = wr * imag[idx2] + wi * real[idx2];
        real[idx2] = real[idx1] - tr;
        imag[idx2] = imag[idx1] - ti;
        real[idx1] += tr;
        imag[idx1] += ti;
      }
    }
  }
}

/**
 * Compute magnitude spectrum from PCM samples
 * Returns Float64Array of magnitudes for bins 0..fftSize/2-1
 */
function computeSpectrum(samples, fftSize) {
  const real = new Float64Array(fftSize);
  const imag = new Float64Array(fftSize);

  // Apply Hanning window and copy
  const len = Math.min(samples.length, fftSize);
  for (let i = 0; i < len; i++) {
    const w = 0.5 * (1 - Math.cos(2 * Math.PI * i / (fftSize - 1)));
    real[i] = samples[i] * w;
  }

  fft(real, imag);

  const magnitudes = new Float64Array(fftSize / 2);
  for (let i = 0; i < fftSize / 2; i++) {
    magnitudes[i] = Math.sqrt(real[i] * real[i] + imag[i] * imag[i]);
  }
  return magnitudes;
}

/**
 * Detect fundamental frequency using FFT + parabolic interpolation
 */
function detectFrequency(samples, sampleRate) {
  if (samples.length < FFT_SIZE) return 0;

  const magnitudes = computeSpectrum(samples, FFT_SIZE);
  const startBin = Math.floor(MIN_FREQ * FFT_SIZE / sampleRate);
  const endBin = Math.min(Math.ceil(MAX_FREQ * FFT_SIZE / sampleRate), FFT_SIZE / 2);

  let maxMag = 0;
  let maxBin = 0;
  for (let i = startBin; i < endBin; i++) {
    if (magnitudes[i] > maxMag) {
      maxMag = magnitudes[i];
      maxBin = i;
    }
  }

  if (maxMag < 0.01) return 0;

  // Parabolic interpolation for sub-bin accuracy
  if (maxBin > 0 && maxBin < magnitudes.length - 1) {
    const alpha = magnitudes[maxBin - 1];
    const beta = magnitudes[maxBin];
    const gamma = magnitudes[maxBin + 1];
    const denom = alpha - 2 * beta + gamma;
    if (Math.abs(denom) > 1e-10) {
      const p = 0.5 * (alpha - gamma) / denom;
      return (maxBin + p) * sampleRate / FFT_SIZE;
    }
  }

  return maxBin * sampleRate / FFT_SIZE;
}

/**
 * Compute resonance: ratio of harmonic energy (H2-H5) to total energy
 */
function computeResonance(samples, sampleRate, f0) {
  if (f0 <= 0 || samples.length < FFT_SIZE) return 0;

  const magnitudes = computeSpectrum(samples, FFT_SIZE);

  // Total energy in vocal range
  const startBin = Math.floor(MIN_FREQ * FFT_SIZE / sampleRate);
  const endBin = Math.min(Math.ceil(4000 * FFT_SIZE / sampleRate), FFT_SIZE / 2);
  let totalEnergy = 0;
  for (let i = startBin; i < endBin; i++) {
    totalEnergy += magnitudes[i] * magnitudes[i];
  }

  if (totalEnergy < 1e-10) return 0;

  // Harmonic energy: H2-H5
  let harmonicEnergy = 0;
  const binWidth = sampleRate / FFT_SIZE;
  const tolerance = 2; // bins
  for (let h = 2; h <= 5; h++) {
    const harmonicBin = Math.round(f0 * h / binWidth);
    if (harmonicBin >= FFT_SIZE / 2) break;
    for (let b = Math.max(0, harmonicBin - tolerance); b <= Math.min(FFT_SIZE / 2 - 1, harmonicBin + tolerance); b++) {
      harmonicEnergy += magnitudes[b] * magnitudes[b];
    }
  }

  return Math.min(1, harmonicEnergy / totalEnergy);
}

/**
 * Performance rating based on metrics
 */
function ratePerformance(frequency, volume, resonance, isActive) {
  if (!isActive) return 'unavailable';
  if (resonance > 0.4 && volume > 0.15) return 'excellent';
  if (resonance > 0.2 && volume > 0.08) return 'good';
  if (volume > 0.03) return 'neutral';
  return 'warning';
}

/**
 * Main entry: analyze an audio blob locally, return 42-segment metrics
 */
export async function analyzeAudioLocally(audioBlob) {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();

  try {
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Get mono PCM data
    const channelData = audioBuffer.getChannelData(0);
    const sampleRate = audioBuffer.sampleRate;
    const duration = audioBuffer.duration;
    const samplesPerSegment = Math.floor(channelData.length / NUM_SEGMENTS);

    const metrics_42 = [];
    const equation_results = [];
    let f0Sum = 0, volSum = 0, resSum = 0, activeCount = 0;

    for (let i = 0; i < NUM_SEGMENTS; i++) {
      const start = i * samplesPerSegment;
      const end = Math.min(start + samplesPerSegment, channelData.length);
      const segment = channelData.subarray(start, end);

      const startTime = start / sampleRate;
      const endTime = end / sampleRate;

      const volume = computeRMS(segment);
      const isActive = volume > NOISE_VOLUME_THRESHOLD;
      const frequency = isActive ? detectFrequency(segment, sampleRate) : 0;
      const resonance = (isActive && frequency > 0) ? computeResonance(segment, sampleRate, frequency) : 0;

      const performance = ratePerformance(frequency, volume, resonance, isActive);

      let score = 0;
      if (isActive && frequency > 0) {
        score = Math.min(100, Math.round(
          (resonance * 40) + (Math.min(volume, 0.5) / 0.5 * 30) + 30
        ));
      }

      metrics_42.push({
        id: i + 1,
        name: `Segment ${i + 1}`,
        frequency: Math.round(frequency * 10) / 10,
        note: frequencyToNote(frequency),
        volume: Math.round(volume * 1000) / 10,
        resonance: Math.round(resonance * 1000) / 10,
        isActive,
        value: frequency > 0 ? `${Math.round(frequency)} Hz` : 'Silence',
        unit: 'Hz',
        description: `${startTime.toFixed(1)}s - ${endTime.toFixed(1)}s`,
        performance,
        category: 'Analyse locale'
      });

      equation_results.push({
        score,
        interpretation: performance === 'excellent' ? 'Signal vocal riche'
          : performance === 'good' ? 'Signal vocal stable'
          : performance === 'neutral' ? 'Signal faible'
          : isActive ? 'Signal instable' : 'Silence'
      });

      if (isActive && frequency > 0) {
        f0Sum += frequency;
        volSum += volume;
        resSum += resonance;
        activeCount++;
      }
    }

    const features = {
      f0_mean: activeCount > 0 ? Math.round(f0Sum / activeCount) : 0,
      volume_mean: activeCount > 0 ? Math.round((volSum / activeCount) * 100) : 0,
      resonance_mean: activeCount > 0 ? Math.round((resSum / activeCount) * 100) : 0,
      duration: Math.round(duration * 10) / 10,
      active_segments: activeCount,
      total_segments: NUM_SEGMENTS
    };

    const activeScores = equation_results.filter((_, i) => metrics_42[i].isActive);
    const globalScore = activeScores.length > 0
      ? Math.round(activeScores.reduce((s, e) => s + e.score, 0) / activeScores.length)
      : 0;

    console.log(`[LocalAnalysis] ${activeCount}/${NUM_SEGMENTS} active segments, F0=${features.f0_mean}Hz, score=${globalScore}%`);

    return {
      metrics_42,
      equation_results,
      features,
      globalScore,
      personalized: {},
      source: 'local'
    };
  } finally {
    await audioContext.close();
  }
}
